import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TimezoneService {

  private readonly countryAliases: Record<string, string> = {
    'españa': 'ES',
    'espana': 'ES',
    'spain': 'ES',
    'es': 'ES',
    'costa rica': 'CR',
    'costa rica ': 'CR',
    'costa roca': 'CR',
    'costarica': 'CR',
    'costrica': 'CR',
    'cr': 'CR',
    'reino unido': 'GB',
    'reinounido': 'GB',
    'united kingdom': 'GB',
    'uk': 'GB',
    'gb': 'GB',
    'italia': 'IT',
    'italy': 'IT',
    'it': 'IT',
    'portugal': 'PT',
    'pt': 'PT',
    'alemania': 'DE',
    'germany': 'DE',
    'de': 'DE',
    'estados unidos': 'US',
    'estadosunidos': 'US',
    'united states': 'US',
    'usa': 'US',
    'us': 'US',
    'chile': 'CL',
    'cl': 'CL',
    'taiwan': 'TW',
    'taiwán': 'TW',
    'tw': 'TW',
  };

  private readonly countryConfig: Record<string, { timezone: string; locale: string; dateFormat: string }> = {
    ES: { timezone: 'Europe/Madrid', locale: 'es-ES', dateFormat: 'dd/MM/yyyy' },
    PT: { timezone: 'Europe/Lisbon', locale: 'pt-PT', dateFormat: 'dd/MM/yyyy' },
    CR: { timezone: 'America/Costa_Rica', locale: 'es-CR', dateFormat: 'dd/MM/yyyy' },
    GB: { timezone: 'Europe/London', locale: 'en-GB', dateFormat: 'dd/MM/yyyy' },
    IT: { timezone: 'Europe/Rome', locale: 'it-IT', dateFormat: 'dd/MM/yyyy' },
    DE: { timezone: 'Europe/Berlin', locale: 'de-DE', dateFormat: 'dd.MM.yyyy' },
    US: { timezone: 'America/New_York', locale: 'en-US', dateFormat: 'MM/dd/yyyy' },
    CL: { timezone: 'America/Santiago', locale: 'es-CL', dateFormat: 'dd/MM/yyyy' },
    TW: { timezone: 'Asia/Taipei', locale: 'zh-TW', dateFormat: 'yyyy/MM/dd' },
  };

  private normalizeCountryCode(country: string): string {
    const normalizedCountry = (country ?? '').trim().toLowerCase();
    if (!normalizedCountry) return 'ES';

    if (/^[a-z]{2}$/i.test(normalizedCountry)) {
      const upper = normalizedCountry.toUpperCase();
      return this.countryConfig[upper] ? upper : 'ES';
    }

    return this.countryAliases[normalizedCountry] ?? 'ES';
  }

  private getTimezoneByCountry(country: string): string {
    const code = this.normalizeCountryCode(country);
    return this.countryConfig[code]?.timezone ?? this.countryConfig['ES'].timezone;
  }

  private getLocaleByCountry(country: string): string {
    const code = this.normalizeCountryCode(country);
    return this.countryConfig[code]?.locale ?? this.countryConfig['ES'].locale;
  }

  convertUtcToLocal(utcDateTimeString: string, country: string): Date {
    const normalized = (utcDateTimeString ?? '').trim();
    if (!normalized) return new Date(NaN);

    const hasTimezone = /(?:Z|[+\-]\d{2}:\d{2})$/i.test(normalized);
    const utcString = hasTimezone ? normalized : `${normalized}Z`;
    const utcDate = new Date(utcString);

    if (Number.isNaN(utcDate.getTime())) {
      return new Date(NaN);
    }

    const timezone = this.getTimezoneByCountry(country);

    const dtf = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = dtf.formatToParts(utcDate);
    const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? '0');

    const y = get('year');
    const m = get('month') - 1;
    const d = get('day');
    const h = get('hour');
    const min = get('minute');
    const s = get('second');

    return new Date(y, m, d, h, min, s);
  }

  convertLocalToUtc(dateString: string, timeString: string, country: string): string {
    const timezone = this.getTimezoneByCountry(country);

    const [year, month, day] = dateString.split('-').map(Number);
    const [hour, minute] = timeString.split(':').map(Number);

    let utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));

    let iteration = 0;
    while (iteration < 25) {
      const localTimeInTarget = utcDate.toLocaleString('sv-SE', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const [localDate, localTime] = localTimeInTarget.split(' ');
      const [localYear, localMonth, localDay] = localDate.split('-').map(Number);
      const [localHour, localMinute] = localTime.split(':').map(Number);

      if (localYear === year && localMonth === month && localDay === day &&
        localHour === hour && localMinute === minute) {
        return utcDate.toISOString();
      }

      const targetTime = hour * 60 + minute;
      const currentTime = localHour * 60 + localMinute;
      const timeDiff = targetTime - currentTime;

      utcDate = new Date(utcDate.getTime() + (timeDiff * 60 * 1000));

      iteration++;
    }

    return utcDate.toISOString();
  }

  formatDateForCenter(date: Date, country: string, options?: Intl.DateTimeFormatOptions): string {
    const timezone = this.getTimezoneByCountry(country);
    const locale = this.getLocaleByCountry(country);

    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };

    const formatOptions = { ...defaultOptions, ...options };

    return date.toLocaleString(locale, formatOptions);
  }

  getCenterTimezone(country: string): string {
    return this.getTimezoneByCountry(country);
  }

  getDateFormatByCountry(country: string): string {
    const code = this.normalizeCountryCode(country);
    return this.countryConfig[code]?.dateFormat ?? this.countryConfig['ES'].dateFormat;
  }

  convertUtcToLocalComponents(utcDateTimeString: string, country: string): { date: string, time: string } {
    const localDate = this.convertUtcToLocal(utcDateTimeString, country);

    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;
    const time = localDate.toTimeString().slice(0, 5);

    return { date, time };
  }

  createLocalDateTime(dateString: string, timeString: string, country: string): Date {
    const timezone = this.getTimezoneByCountry(country);
    const dateTimeString = `${dateString}T${timeString}:00`;

    const tempDate = new Date(dateTimeString);

    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(tempDate);
    const localYear = parseInt(parts.find(p => p.type === 'year')?.value || '0');
    const localMonth = parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1;
    const localDay = parseInt(parts.find(p => p.type === 'day')?.value || '0');
    const localHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
    const localMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
    const localSecond = parseInt(parts.find(p => p.type === 'second')?.value || '0');

    return new Date(localYear, localMonth, localDay, localHour, localMinute, localSecond);
  }
} 